import { AllJointSystemReadModel, AllMarginalContributorReadModel, LabCaseRunPrintOptionsModel, TopMarginalSystemReadModel } from '@app/api2/api';
import { BPBoxWithHeader } from '@app/components/React/Common/BPBoxWithHeader';
import { BPTabPane } from '@app/components/React/Common/BPTabPane';
import { CleanTable } from '@app/components/React/Common/CleanTable';
import { PageBreak } from '@app/components/React/Common/PageBreak';
import { isNullOrWhitespace, selectMany, toSci } from '@app/components/React/Common/Utils';
import * as React from 'react';
import { Tab, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';

interface IDeconvolutionProps {
    model: {
        type: string;
        topMarginalSystems?: TopMarginalSystemReadModel[] | undefined;
        allJointSystems?: AllJointSystemReadModel[] | undefined;
        allMarginalContributors?: AllMarginalContributorReadModel[];
    }
    highlightAbove?: number;
    forPrint?: LabCaseRunPrintOptionsModel;
}

export const Deconvolution: React.FunctionComponent<IDeconvolutionProps> = (props) => {
    const deconvAllJoint = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllJoint : props.forPrint.hdDeconvAllJoint : true;
    const deconvAllMarginalAlleles = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllMarginalAlleles : props.forPrint.hdDeconvAllMarginalAlleles : true;
    const deconvAllMarginalGeno = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllMarginalGeno : props.forPrint.hdDeconvAllMarginalGeno : true;
    const deconvTopMarginal = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvTopMarginal : props.forPrint.hdDeconvTopMarginal : true;


    const panesDeconvolution = props.model.topMarginalSystems?.length > 0 ? [
        ...deconvTopMarginal ? [{ menuItem: window.translation.caseRunView.topMarginal, pane: <BPTabPane key={`${props.model.type}_topmarginalpane`}><TopMarginal forPrint={props.forPrint != null} type={props.model.type} topMarginalSystems={props.model.topMarginalSystems} highlightAbove={props.highlightAbove} /></BPTabPane> }] : [],
        ...deconvAllJoint ? [{ menuItem: window.translation.caseRunView.allJoint, pane: <BPTabPane key={`${props.model.type}_alljoint`}><AllJoint forPrint={props.forPrint != null} type={props.model.type} allJointSystems={props.model.allJointSystems} /></BPTabPane> }] : [],
        ...deconvAllMarginalGeno && props.model.allMarginalContributors ? [{ menuItem: window.translation.caseRunView.allMarginalGenotypes, pane: <BPTabPane key={`${props.model.type}_allmarginalgeno`}><AllMarginalGenotypes forPrint={props.forPrint != null} type={props.model.type} allMarginalContributors={props.model.allMarginalContributors} highlightAbove={props.highlightAbove} /></BPTabPane> }] : [],
        ...deconvAllMarginalAlleles && props.model.allMarginalContributors ? [{ menuItem: window.translation.caseRunView.allMarginalAlleles, pane: <BPTabPane key={`${props.model.type}_allmarginalaleles`}><AllMarginalAlleles forPrint={props.forPrint != null} type={props.model.type} allMarginalContributors={props.model.allMarginalContributors} highlight={props.highlightAbove} /></BPTabPane> }] : [],
    ] : [];

    return !props.forPrint ? <>
        {panesDeconvolution.length > 0 &&
            <Tab
                renderActiveOnly={false}
                panes={panesDeconvolution}
                menu={{ secondary: true, pointing: true }} />}
        {panesDeconvolution.length == 0 &&
            <div>{window.translation.caseRunView.noDeconvolution}</div>}
    </> : <>
        {panesDeconvolution.map((x, idx) => (<React.Fragment key={`${props.model.type}_${x.menuItem}_pane`} >
            {idx != 0 && <PageBreak key={`${props.model.type}_${x.menuItem}_pagebreak`} />}
            <BPBoxWithHeader key={`${props.model.type}_${x.menuItem}_header`} forPrint={props.forPrint != null} header={x.menuItem}>
                {x.pane}
            </BPBoxWithHeader>
        </React.Fragment>
        ))}
    </>;
};


interface ITopMarginalProps {
    type: string;// 'p' | 'd'; 
    // model: ResultHypothesisReadModel;
    highlightAbove?: number;
    topMarginalSystems: TopMarginalSystemReadModel[]
    forPrint?: boolean;
}

export function TopMarginal(props: ITopMarginalProps) {
    return <Table as={props.forPrint ? CleanTable : Table} compact striped>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>{window.translation.common.system}</TableHeaderCell>
                <TableHeaderCell>{window.translation.common.contributor}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.genotype}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.probability}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.ratioToNext}</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableRow>
        </TableHeader>

        {props.topMarginalSystems.map((s, idx) => {
            return (
                <TableBody key={`${props.type}_${s.system}_${idx}`}>
                    {s.contributors.sort((a, b) => a.contributor.localeCompare(b.contributor)).map((c, i) => {
                        const highlightClass = props.highlightAbove && isNullOrWhitespace(c.sampleName) && c.probability >= props.highlightAbove ? "positive" : "";


                        return (
                            <TableRow key={`${props.type}_${s.system}_${idx}_${c.contributor}_${i}`}>
                                {i == 0 &&
                                    <TableCell rowSpan={s.contributors.length}>{s.system}</TableCell>}
                                <TableCell className={highlightClass}><abbr title={c.sampleName}>{c.contributor}</abbr></TableCell>
                                <TableCell className={highlightClass}>{c.genotype}</TableCell>
                                <TableCell className={highlightClass}>{toSci(c.probability)}</TableCell>
                                <TableCell className={highlightClass}>{toSci(c.ratioToNext) ?? '-'}</TableCell>
                                <TableCell className={highlightClass}>{!isNullOrWhitespace(highlightClass) &&
                                    <i className="icon checkmark"></i>}</TableCell>
                            </TableRow>

                        );
                    })}
                </TableBody>
            );
        })}

    </Table>;
}


interface IAllJointProps {
    type: string;
    // model: ResultHypothesisReadModel;
    allJointSystems: AllJointSystemReadModel[]
    forPrint?: boolean;


}

export function AllJoint(props: IAllJointProps) {
    return <Table as={props.forPrint ? CleanTable : Table} compact striped>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>{window.translation.common.system}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.rank}</TableHeaderCell>
                {props.allJointSystems[0]?.ranks[0]?.contributors.sort((a, b) => a.contributor < b.contributor ? -1 : 1).map(c => (
                    <TableHeaderCell key={`aj_${c.contributor}`}><abbr title={c.sampleName}>{c.contributor}</abbr></TableHeaderCell>
                ))}
                <TableHeaderCell>{window.translation.caseRunView.probability}</TableHeaderCell>
            </TableRow>
        </TableHeader>

        {props.allJointSystems.map(s => {
            return (
                <TableBody key={`${props.type}_${s.system}`}>
                    {s.ranks.sort((a, b) => a.rank - b.rank).map((r, i) => (
                        <TableRow key={`${props.type}_${r.rank}_${s.system}`}>
                            {i == 0 &&
                                <TableCell rowSpan={s.ranks.length}>{s.system}</TableCell>}
                            <TableCell>{r.rank}</TableCell>
                            {r.contributors.sort((a, b) => a.contributor < b.contributor ? -1 : 1).map(c => (
                                <TableCell key={`aj_${s.system}_${r.rank}_${c.contributor}`}>{c.genotype}</TableCell>
                            ))}
                            <TableCell>{r.probability}</TableCell>
                        </TableRow>

                    ))}
                </TableBody>
            );
        })}

    </Table>;
}


interface IAllMarginalGenotypesProps {
    type: string;
    //  model: ResultHypothesisReadModel;
    allMarginalContributors: Array<AllMarginalContributorReadModel>

    highlightAbove?: number;
    forPrint?: boolean;

}

export const AllMarginalGenotypes: React.FunctionComponent<IAllMarginalGenotypesProps> = (props) => {
    const contributors = props.allMarginalContributors.filter(x => x.marginalType == 'g')
        .sort((x, y) => x.contributor.localeCompare(y.contributor)).map((c, ci) => {
            return c;
        })

    return <Table as={props.forPrint ? CleanTable : Table} compact striped>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>{window.translation.common.contributor}</TableHeaderCell>
                <TableHeaderCell>{window.translation.common.system}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.genotype}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.probability}</TableHeaderCell>
            </TableRow>
        </TableHeader>

        {contributors.map((c, ci) => {
            return (
                <TableBody key={`${c.marginalType}_${ci}_${c.contributor}`}>
                    {c.systems.map((s, si) => {
                        return s.values.sort((a, b) => a.probability - b.probability || a.value.localeCompare(b.value)).map((v, vi) => {
                            const highlightClass = props.highlightAbove && isNullOrWhitespace(c.sampleName) && v.probability >= props.highlightAbove ? "positive" : "";

                            return (
                                <TableRow key={`${c.marginalType}_${s.system}_${c.contributor}_${v.value}`}>
                                    {si == 0 && vi == 0 &&
                                        <TableCell rowSpan={selectMany(c.systems, x => x.values).length}>{c.contributor}</TableCell>
                                    }
                                    {vi == 0 &&
                                        <TableCell rowSpan={s.values.length}>{s.system}</TableCell>
                                    }
                                    <TableCell className={highlightClass}>{v.value}</TableCell>
                                    <TableCell className={highlightClass}>{v.probability}</TableCell>
                                    <TableCell className={highlightClass}>{!isNullOrWhitespace(highlightClass) &&
                                        <i className="icon checkmark"></i>
                                    }</TableCell>
                                </TableRow>
                            )
                        })
                    })}
                </TableBody>
            )
        })
        }

    </Table>;
};

interface IAllMarginalAllelesProps {
    type: string;
    // model: ResultHypothesisReadModel;
    allMarginalContributors: AllMarginalContributorReadModel[]

    highlight?: number;
    forPrint?: boolean;

}

export const AllMarginalAlleles: React.FunctionComponent<IAllMarginalAllelesProps> = (props) => {
    const contributors = props.allMarginalContributors.filter(x => x.marginalType == 'a')
        .sort((x, y) => x.contributor.localeCompare(y.contributor)).map((c, ci) => {
            return c;
        })

    return <Table as={props.forPrint ? CleanTable : Table} compact striped>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>{window.translation.common.contributor}</TableHeaderCell>
                <TableHeaderCell>{window.translation.common.system}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.genotype}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.probability}</TableHeaderCell>
            </TableRow>
        </TableHeader>

        {contributors.map((c, ci) => {
            return (
                <TableBody key={`${c.marginalType}_${ci}_${c.contributor}`}>
                    {c.systems.map((s, si) => {
                        return s.values.sort((a, b) => a.probability - b.probability || a.value.localeCompare(b.value)).map((v, vi) => {
                            const highlight = props.highlight && isNullOrWhitespace(c.sampleName) && v.probability >= props.highlight ? "positive" : "";

                            return (
                                <TableRow key={`${c.marginalType}_${s.system}_${c.contributor}_${v.value}`}>
                                    {si == 0 && vi == 0 &&
                                        <TableCell rowSpan={selectMany(c.systems, x => x.values).length}>{c.contributor}</TableCell>
                                    }
                                    {vi == 0 &&
                                        <TableCell rowSpan={s.values.length}>{s.system}</TableCell>
                                    }
                                    <TableCell className={highlight}>{v.value}</TableCell>
                                    <TableCell className={highlight}>{v.probability}</TableCell>
                                    <TableCell className={highlight}>{!isNullOrWhitespace(highlight) &&
                                        <i className="icon checkmark"></i>
                                    }</TableCell>
                                </TableRow>
                            )
                        })
                    })}
                </TableBody>
            )
        })
        }
    </Table>;
};

